import { AddressLookupQuestion } from "./AddressLookupQuestion";
import { AnnualTurnoverQuestion } from "./AnnualTurnoverQuestion";
import { DateInputQuestion } from "./DateInputQuestion";
import { DateQuestion } from "./DateQuestion";
import { InfoQuestion } from "./InfoQuestion";
import { MultiSelectQuestion } from "./MultiSelectQuestion/MultiSelectQuestion";
import { NumberQuestion } from "./NumberQuestion";
import {
  PercentageQuestion,
  PercentageGroupQuestion,
} from "./PercentageQuestion";
import { RadioButtonsQuestion } from "./RadioButtonsQuestion";
import { SelectQuestion } from "./SelectQuestion/SelectQuestion";
import { TelephoneQuestion } from "./TelephoneQuestion";
import { TextQuestion } from "./TextQuestion";
import { TradeQuestion } from "./TradeQuestion";
import { AddressLookupGroupQuestion } from "./AddressLookupQuestion/AddressLookupGroupQuestion";
import { CurrencyQuestion } from "./CurrencyQuestion";
import { ZipCodeQuestion } from "./ZipCodeQuestion";
import { USStateQuestion } from "./USStateQuestion";
import { PersonalisedCoverQuestion } from "./PersonalisedCoverQuestion";
import { AgreementQuestion } from "./AgreementQuestion";

export const TRIGGERS = {
  CHANGE: "change",
  BLUR: "blur",
} as const;

export const ELEMENT_TYPES = {
  LABEL: "label",
  SPAN: "span",
} as const;

type Trigger = (typeof TRIGGERS)[keyof typeof TRIGGERS];
type ElementType = (typeof ELEMENT_TYPES)[keyof typeof ELEMENT_TYPES];

function entry<C>(
  component: C,
  trigger: Trigger,
  labelElementType: ElementType,
) {
  return { component, trigger, labelElementType };
}

const { BLUR, CHANGE } = TRIGGERS;
const { LABEL, SPAN } = ELEMENT_TYPES;

function createQuestionMap() {
  return {
    default: entry(TextQuestion, BLUR, LABEL),
    info: entry(InfoQuestion, BLUR, SPAN),
    address_lookup: entry(AddressLookupQuestion, CHANGE, SPAN),
    address_lookup_group: entry(AddressLookupGroupQuestion, CHANGE, SPAN),
    annual_turnover: entry(AnnualTurnoverQuestion, BLUR, SPAN),
    multiselect: entry(MultiSelectQuestion, CHANGE, SPAN),
    radio_buttons: entry(RadioButtonsQuestion, CHANGE, SPAN),
    select: entry(SelectQuestion, CHANGE, LABEL),
    text: entry(TextQuestion, BLUR, LABEL),
    number: entry(NumberQuestion, BLUR, LABEL),
    currency: entry(CurrencyQuestion, BLUR, LABEL),
    tel: entry(TelephoneQuestion, BLUR, LABEL),
    rebuild_cost_estimate: entry(NumberQuestion, CHANGE, LABEL),
    datepicker: entry(DateQuestion, CHANGE, LABEL),
    date: entry(DateInputQuestion, BLUR, LABEL),
    trade_search: entry(TradeQuestion, CHANGE, SPAN),
    trade_search_ai: entry(TradeQuestion, CHANGE, SPAN),
    personalised_cover: entry(PersonalisedCoverQuestion, CHANGE, LABEL),
    // Items below need to be reconciled
    given_name: entry(TextQuestion, BLUR, LABEL),
    family_name: entry(TextQuestion, BLUR, LABEL),
    email: entry(TextQuestion, BLUR, LABEL),
    percentage_split: entry(PercentageQuestion, BLUR, LABEL),
    percentage_split_group: entry(PercentageGroupQuestion, BLUR, LABEL),
    postcode: entry(TextQuestion, BLUR, LABEL),
    zipcode: entry(ZipCodeQuestion, BLUR, LABEL),
    us_state_select: entry(USStateQuestion, CHANGE, LABEL),
    agreement: entry(AgreementQuestion, CHANGE, SPAN),
  };
}

export type QuestionMap = ReturnType<typeof createQuestionMap>;

const cachedMap = createQuestionMap();

export function getQuestionMap(): QuestionMap {
  if (process.env.NODE_ENV === "production") {
    return cachedMap;
  }
  return createQuestionMap();
}
